import { useState } from "react";
import { useList } from "@refinedev/core";
import { Plus, Smartphone, Bike, Edit } from "lucide-react";
import { useForm } from "@refinedev/react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { MoneyInput } from "@/components/forms/MoneyInput";
import { ToggleActivo } from "@/components/forms/ToggleActivo";
import { Link } from "react-router-dom";

interface Producto {
  id: string;
  tipo_producto: "moto" | "celular";
  marca: string;
  modelo: string;
  almacenamiento: string | null;
  precio_referencia: number | null;
  activo: boolean;
}

export function ProductosPage() {
  const [tipo, setTipo] = useState<"moto" | "celular" | "all">("all");
  const { data, isLoading } = useList<Producto>({
    resource: "productos_catalogo",
    filters: tipo !== "all" ? [{ field: "tipo_producto", operator: "eq", value: tipo }] : [],
  });

  return (
    <div>
      <PageHeader
        title="Catálogo de productos"
        subtitle="Motos y celulares"
        actions={
          <Link to="/admin/productos/nuevo" className="btn-primary inline-flex items-center gap-2">
            <Plus className="size-4" /> Nuevo
          </Link>
        }
      />

      <div className="flex gap-2 mb-4">
        {(["all", "moto", "celular"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTipo(t)}
            className={`px-3 py-1.5 rounded-pill text-sm ${
              tipo === t ? "bg-brand text-white" : "bg-bg-card border border-border"
            }`}
          >
            {t === "all" ? "Todos" : t === "moto" ? "Motos" : "Celulares"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="card p-8 text-center text-fg-muted">Cargando...</div>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {data?.data.map((p) => (
            <li key={p.id} className={`card p-4 ${!p.activo ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-2 mb-2">
                {p.tipo_producto === "celular" ? (
                  <Smartphone className="size-4 text-fg-muted" />
                ) : (
                  <Bike className="size-4 text-fg-muted" />
                )}
                <span className="text-xs uppercase text-fg-muted">{p.marca}</span>
                {!p.activo && (
                  <span className="text-xs text-error ml-auto">Inactivo</span>
                )}
              </div>
              <p className="font-medium">{p.modelo}</p>
              {p.almacenamiento && <p className="text-sm text-fg-muted">{p.almacenamiento}</p>}
              {p.precio_referencia && (
                <MoneyDisplay value={p.precio_referencia} className="text-sm mt-1" />
              )}
              <div className="flex items-center gap-1 pt-2 mt-2 border-t border-border">
                <ToggleActivo
                  resource="productos_catalogo"
                  id={p.id}
                  activo={p.activo}
                  confirmarTexto={`¿Desactivar "${p.marca} ${p.modelo}"?`}
                />
                <Link
                  to={`/admin/productos/${p.id}/editar`}
                  className="p-2 rounded-md hover:bg-bg text-fg-secondary"
                  aria-label="Editar"
                >
                  <Edit className="size-4" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ProductoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const {
    refineCore: { onFinish, formLoading },
    register,
    handleSubmit,
    watch,
    setValue,
  } = useForm({
    refineCoreProps: {
      resource: "productos_catalogo",
      action: isEdit ? "edit" : "create",
      id,
      redirect: false,
      onMutationSuccess: () => navigate("/admin/productos"),
    },
  });

  const tipo = watch("tipo_producto");
  const precio = watch("precio_referencia");

  return (
    <div className="max-w-xl">
      <PageHeader title={isEdit ? "Editar producto" : "Nuevo producto"} />
      <form onSubmit={handleSubmit(onFinish)} className="card p-6 space-y-4">
        <Field label="Tipo">
          <select {...register("tipo_producto", { required: true })} className="input">
            <option value="">Selecciona...</option>
            <option value="moto">Moto</option>
            <option value="celular">Celular</option>
          </select>
        </Field>
        <Field label="Marca">
          <input {...register("marca", { required: true })} className="input" />
        </Field>
        <Field label="Modelo">
          <input {...register("modelo", { required: true })} className="input" />
        </Field>
        {tipo === "celular" && (
          <>
            <Field label="Almacenamiento">
              <input {...register("almacenamiento")} className="input" placeholder="128GB" />
            </Field>
            <Field label="RAM">
              <input {...register("ram")} className="input" placeholder="8GB" />
            </Field>
          </>
        )}
        <Field label="Precio de referencia">
          <MoneyInput
            value={precio as number}
            onChange={(v) => setValue("precio_referencia", v)}
          />
        </Field>

        <footer className="flex gap-2 pt-2">
          <button type="submit" disabled={formLoading} className="btn-primary">
            {formLoading ? "Guardando..." : "Guardar"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">
            Cancelar
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
